import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppTheme from "../../../theme/AppTheme";
import {
  CssBaseline,
  Box,
  Typography,
  Button,
  TextField,
  MenuItem,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  Divider,
  Paper,
  IconButton,
  Alert,
  Autocomplete,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  notifyError,
  notifySuccess,
} from "../../../components/ToastContainer/Toast/Toast";
import {
  createOffer,
  publishOffer,
  uploadOfferBanner,
  getStoreOptions,
  getItemOptions,
  refreshStoreCatalog,
  OfferItemPayload,
  OfferPayload,
  StoreOption,
  ItemOption,
  FulfilmentMode,
  PaymentMode,
  DeliveryChargeMode,
} from "../../../service/BuyerConfiguration/OfferManagement/offerService";

/**
 * Create Offer.
 *
 * An offer is a fixed combo from one store at a fixed price with hard
 * inventory — not a promo code. Slots and one-claim-per-user are enforced by
 * the Buyer API (Redis + a partial unique index), not here; this form only
 * has to send a well-formed payload.
 *
 * datetime-local inputs are wall-clock with no zone, so they are converted to
 * ISO with the browser's offset before sending. The server stores timestamptz.
 */
const CreateOffer = (props: { disableCustomTheme?: boolean }) => {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);

  const [name, setName] = useState("");
  const [subtitle, setSubtitle] = useState("");
  const [description, setDescription] = useState("");
  const [bannerUrl, setBannerUrl] = useState("");
  // The image is uploaded AFTER the offer is created, because the upload
  // endpoint needs an offer id to key the S3 object and to write the URL back.
  const [bannerFile, setBannerFile] = useState<File | null>(null);
  const [bannerPreview, setBannerPreview] = useState<string | null>(null);
  const [storeId, setStoreId] = useState<string>("");
  const [storeOptions, setStoreOptions] = useState<StoreOption[]>([]);
  const [itemOptions, setItemOptions] = useState<ItemOption[]>([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [refreshingCatalog, setRefreshingCatalog] = useState(false);

  const unwrap = (res: any) => {
    const payload = res?.data ?? res;
    return Array.isArray(payload) ? payload : (payload?.data ?? []);
  };

  useEffect(() => {
    getStoreOptions()
      .then((res) => setStoreOptions(unwrap(res)))
      .catch(() => notifyError("Could not load stores"));
  }, []);

  // Items belong to the chosen store, so the list reloads whenever it changes
  // and any already-picked items are cleared — they would belong to the wrong
  // store otherwise.
  const loadItems = useCallback(() => {
    if (!storeId) {
      setItemOptions([]);
      return;
    }
    setLoadingItems(true);
    getItemOptions(storeId)
      .then((res) => setItemOptions(unwrap(res)))
      .catch(() => notifyError("Could not load items for this store"))
      .finally(() => setLoadingItems(false));
  }, [storeId]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  /**
   * A dish added in the seller panel is not in the buyer catalogue until ONDC
   * ingestion runs, and only then can it go in an offer. ONDC answers the
   * SEARCH asynchronously, so the item list is re-read after a short pause
   * rather than immediately.
   */
  const handleRefreshCatalog = async () => {
    if (!storeId) return;
    setRefreshingCatalog(true);
    try {
      const res: any = await refreshStoreCatalog(storeId);
      const body = unwrap(res) ?? {};
      if (body.ack_status && body.ack_status !== "ACK") {
        notifyError(
          body.message || "ONDC rejected the refresh. The menu was not updated.",
        );
        return;
      }
      notifySuccess("Menu refresh requested — checking for new items…");
      setTimeout(loadItems, 8000);
    } catch (e: any) {
      notifyError(e?.response?.data?.message || "Could not refresh the menu");
    } finally {
      setRefreshingCatalog(false);
    }
  };

  const [offerPrice, setOfferPrice] = useState<string>("");
  const [strikePrice, setStrikePrice] = useState<string>("");

  const [fulfilmentMode, setFulfilmentMode] = useState<FulfilmentMode>("direct");
  const [bookingOpensAt, setBookingOpensAt] = useState("");
  const [bookingClosesAt, setBookingClosesAt] = useState("");
  const [deliveryStartsAt, setDeliveryStartsAt] = useState("");

  const [maxClaims, setMaxClaims] = useState<string>("50");
  const [perUserLimit, setPerUserLimit] = useState<string>("1");

  const [paymentMode, setPaymentMode] = useState<PaymentMode>("online_only");
  const [cancellationAllowed, setCancellationAllowed] = useState(false);

  const [chargeMode, setChargeMode] = useState<DeliveryChargeMode>("standard");
  const [chargeValue, setChargeValue] = useState<string>("");
  const [maxKm, setMaxKm] = useState<string>("");
  // Blank = normal tax rules, 0 = no tax, 5 = 5% of subtotal.
  const [taxPercent, setTaxPercent] = useState<string>("");

  const [items, setItems] = useState<OfferItemPayload[]>([
    { item_id: 0, quantity: 1, name_snapshot: "" },
  ]);

  const [publishNow, setPublishNow] = useState(false);

  const isPrebooking = fulfilmentMode === "prebooking";

  const totalPreview = useMemo(() => {
    const price = Number(offerPrice) || 0;
    if (chargeMode === "free") return `₹${price} + free delivery = ₹${price}`;
    if (chargeMode === "per_km")
      return `₹${price} + ₹${chargeValue || 0}/km delivery`;
    const chg = Number(chargeValue) || 0;
    return `₹${price} + ₹${chg} delivery = ₹${price + chg}`;
  }, [offerPrice, chargeMode, chargeValue]);

  const updateItem = (idx: number, patch: Partial<OfferItemPayload>) =>
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));

  const addItem = () =>
    setItems((prev) => [...prev, { item_id: 0, quantity: 1, name_snapshot: "" }]);

  const removeItem = (idx: number) =>
    setItems((prev) => prev.filter((_, i) => i !== idx));

  /** datetime-local -> ISO 8601 carrying the browser's offset. */
  const toIso = (local: string) => (local ? new Date(local).toISOString() : undefined);

  const validate = (): string | null => {
    if (!name.trim()) return "Offer name is required";
    if (!storeId) return "Select a store";
    if (!offerPrice) return "Offer price is required";
    if (!items.length || items.some((i) => !i.item_id || !i.name_snapshot.trim()))
      return "Every combo item needs an item ID and a name";
    if (!maxClaims || Number(maxClaims) < 1) return "Max claims must be at least 1";

    if (isPrebooking) {
      if (!bookingOpensAt || !bookingClosesAt || !deliveryStartsAt)
        return "Pre-booking needs booking open, booking close and delivery start times";
      if (new Date(bookingOpensAt) >= new Date(bookingClosesAt))
        return "Booking must open before it closes";
      if (new Date(bookingClosesAt) > new Date(deliveryStartsAt))
        return "Delivery must start at or after booking closes";
    }

    if (chargeMode !== "free" && !chargeValue)
      return "Delivery charge value is required for standard and per-km modes";

    return null;
  };

  const handleSubmit = async () => {
    const error = validate();
    if (error) {
      notifyError(error);
      return;
    }

    const payload: OfferPayload = {
      name: name.trim(),
      subtitle: subtitle || undefined,
      description: description || undefined,
      banner_image_url: bannerUrl || undefined,
      store_id: Number(storeId),
      offer_price: Number(offerPrice),
      strike_price: strikePrice ? Number(strikePrice) : undefined,
      fulfilment_mode: fulfilmentMode,
      booking_opens_at: isPrebooking ? toIso(bookingOpensAt) : undefined,
      booking_closes_at: isPrebooking ? toIso(bookingClosesAt) : undefined,
      delivery_starts_at: isPrebooking ? toIso(deliveryStartsAt) : undefined,
      max_claims: Number(maxClaims),
      per_user_limit: Number(perUserLimit) || 1,
      payment_mode: paymentMode,
      cancellation_allowed: cancellationAllowed,
      delivery_charge_mode: chargeMode,
      delivery_charge_value: chargeMode !== "free" ? Number(chargeValue) : undefined,
      max_delivery_km: maxKm ? Number(maxKm) : undefined,
      tax_percent: taxPercent === "" ? undefined : Number(taxPercent),
      items: items.map((i) => ({
        item_id: Number(i.item_id),
        variant_id: i.variant_id ? Number(i.variant_id) : undefined,
        quantity: Number(i.quantity),
        name_snapshot: i.name_snapshot.trim(),
        price_snapshot: i.price_snapshot ? Number(i.price_snapshot) : undefined,
      })),
    };

    setSaving(true);
    try {
      const res = await createOffer(payload);
      const created = res?.data ?? res;
      notifySuccess("Offer created as draft");

      // Upload after creation: the endpoint keys the S3 object by offer id and
      // writes the resulting URL back onto the offer. Publish happens after,
      // so the banner row is created with the final image already in place.
      if (bannerFile && created?.id) {
        try {
          await uploadOfferBanner(created.id, bannerFile);
        } catch (uploadError: any) {
          // The offer itself is saved — don't lose it over a failed image.
          notifyError(
            `Offer saved, but the banner image failed to upload: ${
              uploadError?.message ?? "unknown error"
            }`,
          );
        }
      }

      if (publishNow && created?.id) {
        await publishOffer(created.id);
        notifySuccess("Offer published — now visible in the buyer app");
      }
      navigate("/offer-management");
    } catch (e: any) {
      notifyError(
        e?.response?.data?.message || e?.message || "Failed to create offer",
      );
    } finally {
      setSaving(false);
    }
  };

  const section = (title: string, hint?: string) => (
    <Box sx={{ mt: 3, mb: 1 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
        {title}
      </Typography>
      {hint && (
        <Typography variant="caption" sx={{ opacity: 0.7 }}>
          {hint}
        </Typography>
      )}
    </Box>
  );

  return (
    <AppTheme {...props}>
      <CssBaseline enableColorScheme />
      <Box sx={{ p: 2, maxWidth: 900 }}>
        <Typography variant="h5">Create Offer</Typography>
        <Typography variant="body2" sx={{ opacity: 0.7, mb: 2 }}>
          A fixed combo from one store at a fixed price, with limited slots.
        </Typography>

        <Paper sx={{ p: 3 }}>
          {section("1. Basics")}
          <TextField
            fullWidth
            required
            label="Offer Name"
            placeholder="Independence Day ₹79 Combo"
            value={name}
            onChange={(e) => setName(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Subtitle"
            placeholder="Chief Warriors • Othakadai"
            value={subtitle}
            onChange={(e) => setSubtitle(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            multiline
            minRows={2}
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            sx={{ mb: 2 }}
          />
          <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
            <Box>
              <Button variant="outlined" component="label">
                {bannerFile ? "Change Banner Image" : "Upload Banner Image"}
                <input
                  hidden
                  accept="image/*"
                  type="file"
                  onChange={(e) => {
                    const file = e.target.files?.[0] ?? null;
                    setBannerFile(file);
                    setBannerPreview(file ? URL.createObjectURL(file) : null);
                  }}
                />
              </Button>
              <Typography
                variant="caption"
                sx={{ display: "block", opacity: 0.7, mt: 0.5 }}
              >
                Shown on the app home and added to Banner Management on publish.
              </Typography>
            </Box>
            {bannerPreview && (
              <Box
                component="img"
                src={bannerPreview}
                alt="Banner preview"
                sx={{
                  width: 180,
                  height: 90,
                  objectFit: "cover",
                  borderRadius: 1,
                  border: "1px solid #E5E7EB",
                }}
              />
            )}
          </Box>
          <TextField
            fullWidth
            label="Banner Image URL (optional)"
            helperText="Only needed if you are not uploading a file above"
            value={bannerUrl}
            onChange={(e) => setBannerUrl(e.target.value)}
            sx={{ mt: 2 }}
          />

          <Divider sx={{ mt: 3 }} />
          {section("2. Store")}
          <Autocomplete
            options={storeOptions}
            // Locality is part of the label because several branches share a
            // name; without it two entries read identically apart from the id.
            // The menu size is appended because duplicate rows can share a
            // name AND a locality; the one with no items is the dead row, and
            // attaching an offer to it fails only later, in the customer's cart.
            getOptionLabel={(o) =>
              [o.name, o.locality, o.city].filter(Boolean).join(", ") +
              ` (#${o.id})` +
              (o.item_count === undefined
                ? ""
                : ` · ${o.item_count} item${o.item_count === 1 ? "" : "s"}`)
            }
            isOptionEqualToValue={(o, v) => o.id === v.id}
            value={storeOptions.find((s) => String(s.id) === storeId) ?? null}
            onChange={(_, picked) => {
              setStoreId(picked ? String(picked.id) : "");
              // Items are store-specific; drop any previous picks.
              setItems([{ item_id: 0, quantity: 1, name_snapshot: "" }]);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                required
                label="Store"
                helperText="The store giving this offer"
              />
            )}
          />

          {storeId && (
            <Button
              size="small"
              onClick={handleRefreshCatalog}
              disabled={refreshingCatalog}
              sx={{ mt: 1 }}
            >
              {refreshingCatalog
                ? "Refreshing menu…"
                : "Item missing? Refresh menu from ONDC"}
            </Button>
          )}

          <Divider sx={{ mt: 3 }} />
          {section("3. Combo Items", "What the customer receives for the offer price")}
          {items.map((item, idx) => (
            <Box key={idx} sx={{ display: "flex", gap: 1, mb: 1.5 }}>
              <Autocomplete
                sx={{ flex: 1 }}
                options={itemOptions}
                loading={loadingItems}
                disabled={!storeId}
                getOptionLabel={(o) => `${o.name} (#${o.id})`}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                value={itemOptions.find((o) => o.id === item.item_id) ?? null}
                onChange={(_, picked) =>
                  updateItem(idx, {
                    item_id: picked?.id ?? 0,
                    // Snapshot the name now — if the dish is renamed later,
                    // customers who booked must still see what they bought.
                    name_snapshot: picked?.name ?? "",
                  })
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Item"
                    placeholder={storeId ? "Search items" : "Select a store first"}
                  />
                )}
              />
              <TextField
                type="number"
                label="Qty"
                value={item.quantity}
                onChange={(e) => updateItem(idx, { quantity: Number(e.target.value) })}
                sx={{ width: 90 }}
              />
              <IconButton
                onClick={() => removeItem(idx)}
                disabled={items.length === 1}
                aria-label="Remove item"
              >
                <DeleteOutlineIcon />
              </IconButton>
            </Box>
          ))}
          <Button size="small" onClick={addItem}>
            + Add item
          </Button>

          <Divider sx={{ mt: 3 }} />
          {section("4. Pricing")}
          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField
              required
              type="number"
              label="Offer Price (₹)"
              value={offerPrice}
              onChange={(e) => setOfferPrice(e.target.value)}
            />
            <TextField
              type="number"
              label="Strike Price (₹)"
              helperText="Shown struck through"
              value={strikePrice}
              onChange={(e) => setStrikePrice(e.target.value)}
            />
          </Box>
          <Alert severity="info" sx={{ mt: 2 }}>
            Customer pays: <strong>{totalPreview}</strong>
          </Alert>

          <Divider sx={{ mt: 3 }} />
          {section("5. Fulfilment")}
          <FormControl>
            <RadioGroup
              row
              value={fulfilmentMode}
              onChange={(e) => setFulfilmentMode(e.target.value as FulfilmentMode)}
            >
              <FormControlLabel value="direct" control={<Radio />} label="Direct delivery" />
              <FormControlLabel value="prebooking" control={<Radio />} label="Pre-booking" />
            </RadioGroup>
          </FormControl>

          {isPrebooking && (
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mt: 1 }}>
              <TextField
                required
                type="datetime-local"
                label="Booking opens"
                InputLabelProps={{ shrink: true }}
                value={bookingOpensAt}
                onChange={(e) => setBookingOpensAt(e.target.value)}
              />
              <TextField
                required
                type="datetime-local"
                label="Booking closes"
                InputLabelProps={{ shrink: true }}
                value={bookingClosesAt}
                onChange={(e) => setBookingClosesAt(e.target.value)}
              />
              <TextField
                required
                type="datetime-local"
                label="Delivery starts"
                InputLabelProps={{ shrink: true }}
                helperText="Date and time delivery begins"
                value={deliveryStartsAt}
                onChange={(e) => setDeliveryStartsAt(e.target.value)}
              />
            </Box>
          )}

          <Divider sx={{ mt: 3 }} />
          {section("6. Limits & Rules")}
          <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
            <TextField
              required
              type="number"
              label="Max claims"
              helperText="Total slots, e.g. 50"
              value={maxClaims}
              onChange={(e) => setMaxClaims(e.target.value)}
            />
            <TextField
              type="number"
              label="Per user limit"
              helperText="1 = one claim per customer"
              value={perUserLimit}
              onChange={(e) => setPerUserLimit(e.target.value)}
            />
          </Box>

          <FormControl sx={{ mb: 2, display: "block" }}>
            <FormLabel>Payment</FormLabel>
            <RadioGroup
              row
              value={paymentMode}
              onChange={(e) => setPaymentMode(e.target.value as PaymentMode)}
            >
              <FormControlLabel
                value="online_only"
                control={<Radio />}
                label="Online payment only"
              />
              <FormControlLabel value="both" control={<Radio />} label="Online + COD" />
            </RadioGroup>
            {isPrebooking && paymentMode === "both" && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                COD on a pre-booking offer means a slot is held with no
                commitment — the kitchen cooks for no-shows. Online-only is safer
                for limited-slot pre-bookings.
              </Alert>
            )}
          </FormControl>

          <FormControlLabel
            control={
              <Checkbox
                checked={cancellationAllowed}
                onChange={(e) => setCancellationAllowed(e.target.checked)}
              />
            }
            label="Allow customer cancellation"
          />

          <Divider sx={{ mt: 3 }} />
          {section("7. Delivery Charge")}
          <FormControl>
            <RadioGroup
              value={chargeMode}
              onChange={(e) => setChargeMode(e.target.value as DeliveryChargeMode)}
            >
              <FormControlLabel
                value="standard"
                control={<Radio />}
                label="Standard charge (any distance)"
              />
              <FormControlLabel value="per_km" control={<Radio />} label="Per km" />
              <FormControlLabel value="free" control={<Radio />} label="Free delivery" />
            </RadioGroup>
          </FormControl>

          <Box sx={{ display: "flex", gap: 2, mt: 1 }}>
            {chargeMode !== "free" && (
              <TextField
                required
                type="number"
                label={chargeMode === "per_km" ? "₹ per km" : "Flat charge (₹)"}
                value={chargeValue}
                onChange={(e) => setChargeValue(e.target.value)}
              />
            )}
            <TextField
              type="number"
              label="Max delivery distance (km)"
              helperText="Optional cap"
              value={maxKm}
              onChange={(e) => setMaxKm(e.target.value)}
            />
            <TextField
              type="number"
              label="Tax %"
              helperText="Blank = normal tax · 0 = no tax"
              value={taxPercent}
              onChange={(e) => setTaxPercent(e.target.value)}
            />
          </Box>

          <Divider sx={{ mt: 3 }} />
          {section("8. Publish")}
          <FormControlLabel
            control={
              <Checkbox
                checked={publishNow}
                onChange={(e) => setPublishNow(e.target.checked)}
              />
            }
            label="Sync to buyer app now"
          />
          <Typography variant="caption" sx={{ display: "block", opacity: 0.7 }}>
            Leave unchecked to save as a draft. Drafts are never visible to
            customers and can be published later.
          </Typography>

          <Box sx={{ display: "flex", gap: 2, mt: 3 }}>
            <Button variant="contained" onClick={handleSubmit} disabled={saving}>
              {saving ? "Saving…" : publishNow ? "Create & Publish" : "Save Draft"}
            </Button>
            <Button onClick={() => navigate("/offer-management")} disabled={saving}>
              Cancel
            </Button>
          </Box>
        </Paper>
      </Box>
    </AppTheme>
  );
};

export default CreateOffer;
